import React, { useState, useEffect } from "react";
import CommonSelect from "../../../../../common/CommonSelect/CommonSelect";
import InputFormik from "../../../../../common/Formik/Input/InputFormik";
import "./Widthrow.scss";
import Button from "../../../../../common/Button/Button";
import { useDispatch, useSelector } from "react-redux";
import { FORMIK_REGEX, STATUS_CODE } from "../../../../../../utils/constants";
import {
  getUserCoinListAction,
  withdrawRequestAction,
  generateWithdrawOTPAction,
  getWithdrawHistoryAction,
  getUserInfoAction, // Import the action
} from "../../../../../../features/user/userSlice";
import { setSelectedCoinId } from "../../../../../../features/user/userSlice";
import { toFix } from "../../../../../../Helper/helperfunction";
import { toasts } from "../../../../../common/Toast/Toast";
import { Messages } from "../../../../../../utils/constants";
import { useFormik } from "formik";
import * as Yup from "yup";
import Select from "../../../../../common/Formik/Select/Select";
import selectStyles from "../../../../../common/Formik/Select/Select.module.scss";

const Widthrow = () => {
  const dispatch = useDispatch();
  const storeCoinId = useSelector((state) => state?.users?.selectedCoinId);

  const [selectedCoin, setSelectedCoin] = useState(null);
  const [selectedNetwork, setSelectedNetwork] = useState(null);
  const [coinOptions, setCoinOptions] = useState([]);
  const [withdrawAddress, setWithdrawAddress] = useState("");
  const [otp, setOtp] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [userData, setUserData] = useState(null);
  const [page, setPage] = useState(1);
  const [withdrawData, setWithdrawData] = useState([]);
  const [depositData, setDepositData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPercentage, setSelectedPercentage] = useState(null);
  const decimalLimit = useSelector((state) => state?.users?.coinDecimals);
  const getUserProfile = async () => {
    try {
      const response = await dispatch(getUserInfoAction());
      if (response?.data?.data) {
        setUserData(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
      setError("Failed to fetch user profile.");
    }
  };

  const validationSchema = Yup.object().shape({
    withdrawAmount: Yup.string()
      .required("Required")
      .matches(FORMIK_REGEX.NONZERODECIMAL, Messages?.NONZERODECIMALVALIDATION)
      .test(
        "greater-than-fee",
        "Amount must be greater than withdraw fee",
        function (value) {
          const withdrawFee = selectedCoin?.withdraw_fee || 0;
          return Number(value) > withdrawFee;
        }
      )
      .test(
        "less-than-balance",
        "Amount must be less than or equal to available balance",
        function (value) {
          const balance = selectedCoin?.balance || 0;
          return Number(value) <= balance;
        }
      )
      .test(
        "positive-and-not-zero",
        "Amount must be greater than zero",
        (value) => Number(value) > 0
      ),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      withdrawAmount: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        const withdrawRequestResponse = await dispatch(
          withdrawRequestAction({
            address: withdrawAddress,
            amount: Number(values.withdrawAmount),
            coinId: Number(selectedCoin.value),
            networkId: Number(selectedNetwork.value),
            otp: Number(otp),
          })
        );
        if (withdrawRequestResponse.status === 200) {
          // toasts.success("Withdrawal request successful.");
          fetchCoinOptions();
          withdrawHistory();
          getUserProfile();
          setWithdrawAddress("");
          setOtp("");
          formik.resetForm();
        }
      } catch (error) {
        console.error("Error withdrawing:", error);
        // toasts.error("Failed to process withdrawal.");
      }
    },
  });

  const fetchCoinOptions = async () => {
    try {
      const response = await dispatch(getUserCoinListAction());
      if (response.data.status === STATUS_CODE.successful) {
        const array = response.data.data.coinList.map((coin) => ({
          value: coin.id,
          label: coin.coin_symbol,
          networkdetails: coin.networkDetail,
          minimum_withdraw: coin.minimum_withdraw,
          withdraw_fee: coin.withdraw_fee,
          balance: coin.balance,
        }));
        setCoinOptions(array);

        if (array.length > 0) {
          const defaultCoin = array[0];
          setSelectedCoin(defaultCoin);
          const defaultNetwork = defaultCoin.networkdetails[0];
          setSelectedNetwork({
            value: defaultNetwork.id,
            label: defaultNetwork.parent_coin_name,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching coin options:", error);
      setError("Failed to fetch coin options.");
    }
  };

  const withdrawHistory = async () => {
    setLoading(true);
    try {
      const response = await dispatch(
        getWithdrawHistoryAction({
          page: page,
          limit: 5,
          coinId: selectedCoin?.value,
        })
      );
      if (response.data.status === STATUS_CODE.successful) {
        setWithdrawData(response.data.data.data.withdrawlist);
      }
    } catch (error) {
      setError("Error fetching withdraw history.");
      console.error("Error fetching withdraw history:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCoinOptions();
    getUserProfile();
  }, []);

  useEffect(() => {
    if (selectedCoin) {
      dispatch(setSelectedCoinId(selectedCoin.value));
      withdrawHistory();
    }
  }, [selectedCoin]);

  const handleCoinChange = (selectedCoin) => {
    setSelectedCoin(selectedCoin);
    const newDetails = selectedCoin?.networkdetails;
    setSelectedNetwork(
      newDetails[0]
        ? { value: newDetails[0].id, label: newDetails[0].parent_coin_name }
        : null
    );
  };

  const handlePercentageClick = (percentage) => {
    if (selectedCoin && selectedCoin.balance > 0) {
      const amount = (selectedCoin.balance * percentage) / 100;
      formik.setFieldValue("withdrawAmount", toFix(amount, 5));
      setSelectedPercentage(percentage);
    } else {
      formik.setFieldValue("withdrawAmount", 0);
    }
  };

  const handleGenerateOtp = async () => {
    setOtpLoading(true);
    try {
      const response = await dispatch(
        generateWithdrawOTPAction({
          type: "EMAIL",
          service: "WITHDRAW_REQUEST",
          username: userData?.email,
        })
      );
      if (response.data.status === STATUS_CODE.successful) {
        setOtp(response.data.data.otp);
      }
    } catch (error) {
      console.error("Error generating OTP:", error);
      toasts.error("Failed to generate OTP.");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleAmountChange = (e) => {
    const input = e.target.value;
    const filteredInput = input.replace(/[^0-9.]/g, "");
    const value = filteredInput;
    const regex = new RegExp(
      `^\\d*(\\.\\d{0,${decimalLimit[selectedCoin?.label]}})?$`
    );
    if (value === "" || regex.test(value)) {
      formik.setFieldValue("withdrawAmount", value);
    }
  };

  return (
    <div className="widthrow">
      <div className="widthrow_left">
        <div className="left_inner">
          <div className="inner_select">
            <Select
              variant='xlarge_height'
              className='select_time'
              label="Select Coin"
              options={coinOptions}
              value={selectedCoin}
              onChange={handleCoinChange}
              isSearchable={false}
            />
          </div>
          <div className="inner_select">
            {selectedNetwork && (
              <div
                className={`${selectStyles.select} select_time`}
                data-variant="xlarge_height"
              >
                <label> Network</label>
                <div className={selectStyles.staticReadonlyControl}>
                  <h5 className={selectStyles.staticReadonlyValue}>
                    {selectedNetwork.label}
                  </h5>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="right_inner">
          <InputFormik
            variant='large_height'
            label={
              <>
                Amount{" "}
                <div className="balancedata">
                  Balance : <span>{selectedCoin?.balance || 0}{" "}
                    {selectedCoin?.label || ""}</span>
                </div>
              </>
            }
            placeholder={"Enter Amount"}
            name="withdrawAmount"
            value={formik.values.withdrawAmount}
            onChange={handleAmountChange}
            onBlur={formik.handleBlur}
            formik={formik}
          />
          <p>
            Minimum Withdraw Amount {selectedCoin?.minimum_withdraw || 0}{" "}
            {selectedCoin?.name || ""}
          </p>
          {/* <div className="right_btn_box">
            <button
              className={`percentage-btn ${
                selectedPercentage === 25 ? "active" : ""
              }`}
              onClick={() => handlePercentageClick(25)}
            >
              25%
            </button>
            <button
              className={`percentage-btn ${
                selectedPercentage === 50 ? "active" : ""
              }`}
              onClick={() => handlePercentageClick(50)}
            >
              50%
            </button>
            <button
              className={`percentage-btn ${
                selectedPercentage === 75 ? "active" : ""
              }`}
              onClick={() => handlePercentageClick(75)}
            >
              75%
            </button>
            <button
              className={`percentage-btn ${
                selectedPercentage === 100 ? "active" : ""
              }`}
              onClick={() => handlePercentageClick(100)}
            >
              100%
            </button>
          </div> */}
        </div>
      </div>
      <div className="widthrow_select">
        <InputFormik
          variant='large_height'
          label="Withdraw Address"
          placeholder={"Enter the wallet address"}
          value={withdrawAddress}
          onChange={(e) => setWithdrawAddress(e.target.value)}
        />
      </div>
      <div className="widthrow_select otp_container">
        <InputFormik
          variant='large_height'
          className="otpField"
          label="OTP"
          placeholder="Enter OTP"
          value={otp}
          onChange={(e) => setOtp(e.target.value)}
        />
        <Button
          onClick={handleGenerateOtp}
          loading={otpLoading}
          className="generate_otp_btn"
          variant="small_size without_bg_border"
        >
          Generate OTP
        </Button>
        <div className="withdrawfee">
          <label>Withdraw Fee :</label>
          <p>{selectedCoin?.withdraw_fee || 0}</p>
        </div>
        <p className="withdrawfee_external_note">
          This fee applies to external withdrawals only. Internal transfers do
          not deduct a withdraw fee.
        </p>
      </div>
      {/* <Button fluid onClick={formik.handleSubmit}>
        Withdraw
      </Button> */}
      <Button
        fluid
        onClick={formik.handleSubmit}
        disabled={
          !selectedCoin ||
          !selectedNetwork ||
          !withdrawAddress ||
          !otp ||
          !formik.values.withdrawAmount ||
          !!formik.errors.withdrawAmount ||
          Number(formik.values.withdrawAmount) <
          Number(selectedCoin?.minimum_withdraw || 0)
        }
      >
        Withdraw
      </Button>

      {/* {error && <div className="error-message">{error}</div>} */}
    </div>
  );
};

export default Widthrow;
